import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { getCourseBySlug, getCoursesByCategory, courseCategories } from "@/data/courses";
import { ArrowLeft, BookOpen, Clock, Users, Phone, CheckCircle, ChevronRight, MessageCircle } from "lucide-react";

export default function CourseDetailPage() {
  const { slug } = useParams();
  const course = getCourseBySlug(slug);

  if (!course) {
    return (
      <div className="section-padding text-center" data-testid="course-not-found">
        <div className="container-main">
          <BookOpen size={64} className="text-slate-300 mx-auto mb-4" />
          <h2 className="font-heading text-2xl font-bold text-vedanti-navy mb-2">Course Not Found</h2>
          <p className="text-slate-500 mb-6">The course you&apos;re looking for doesn&apos;t exist.</p>
          <Link to="/courses" className="text-vedanti-orange font-semibold">Browse All Courses</Link>
        </div>
      </div>
    );
  }

  const category = courseCategories.find((c) => c.id === course.category);
  const relatedCourses = getCoursesByCategory(course.category).filter((c) => c.slug !== slug).slice(0, 4);
  const examPattern = course.examPattern;
  const patternKeys = Object.keys(examPattern);

  return (
    <div data-testid="course-detail-page">
      <Helmet>
        <title>{`${course.name} Coaching - ${category?.name || "Competitive Exam"} Preparation | Vedanti Academy`}</title>
        <meta name="description" content={`Best ${course.name} coaching at Vedanti Academy. ${course.description?.slice(0, 120) || `Complete preparation with expert faculty, mock tests, and study material for ${course.name} exam.`}`} />
        <meta name="keywords" content={`${course.name} coaching, ${course.name} preparation, ${course.name} classes Badlapur, ${course.name} exam coaching`} />
        <meta property="og:title" content={`${course.name} Coaching | Vedanti Academy`} />
        <meta property="og:description" content={`Join Vedanti Academy for ${course.name} exam preparation. Expert faculty, updated syllabus & proven results.`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://vedantiacademy.com/courses/${slug}`} />
        <link rel="canonical" href={`https://vedantiacademy.com/courses/${slug}`} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Course",
          "name": `${course.name} Coaching - Vedanti Academy`,
          "description": course.description || `Best ${course.name} coaching at Vedanti Academy with expert faculty and proven results.`,
          "url": `https://vedantiacademy.com/courses/${slug}`,
          "provider": {"@type": "EducationalOrganization", "name": "Vedanti Academy", "url": "https://vedantiacademy.com"},
          "educationalLevel": "Competitive Exam Preparation",
          "courseMode": "Blended"
        })}</script>
      </Helmet>
      {/* Header */}
      <section className="bg-vedanti-navy py-12 md:py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(249,115,22,0.15)_0%,_transparent_60%)]"></div>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-vedanti-orange via-vedanti-mango to-vedanti-orange"></div>
        <div className="container-main relative z-10">
          <Link to="/courses" className="text-vedanti-mango text-sm flex items-center gap-1 mb-4 hover:text-vedanti-orange transition-colors" data-testid="back-to-courses">
            <ArrowLeft size={16} /> Back to All Courses
          </Link>
          <span className="inline-block bg-vedanti-orange/20 text-vedanti-orange text-sm font-semibold px-3 py-1 rounded-full mb-3">
            {category?.name}
          </span>
          <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-white">{course.name}</h1>
          <p className="text-white/80 mt-2 text-lg">{course.fullName}</p>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-main">
          <div className="grid lg:grid-cols-3 gap-10">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-10">
              {/* Overview */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-vedanti-navy mb-4">Course Overview</h2>
                <p className="text-slate-600 leading-relaxed">{course.description}</p>
              </div>

              {/* Eligibility */}
              <div className="bg-slate-50 rounded-xl p-6 border border-slate-100">
                <h3 className="font-heading font-bold text-lg text-vedanti-navy mb-2">Eligibility</h3>
                <p className="text-slate-600">{course.eligibility}</p>
              </div>

              {/* Exam Pattern */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-vedanti-navy mb-6">Exam Pattern</h2>

                {/* Top-level metadata (vacancies + selection process) */}
                {(course.vacancies || course.selectionProcess) && (
                  <div className="grid sm:grid-cols-2 gap-3 mb-6">
                    {course.vacancies && (
                      <div className="bg-orange-50 border-l-4 border-vedanti-orange rounded-r-lg p-3">
                        <p className="text-xs font-semibold text-vedanti-orange uppercase tracking-wider">Vacancies</p>
                        <p className="text-sm font-bold text-vedanti-navy mt-0.5">{course.vacancies}</p>
                      </div>
                    )}
                    {course.selectionProcess && (
                      <div className="bg-blue-50 border-l-4 border-vedanti-navy rounded-r-lg p-3">
                        <p className="text-xs font-semibold text-vedanti-navy uppercase tracking-wider">Selection Process</p>
                        <p className="text-sm text-slate-700 mt-0.5">{course.selectionProcess}</p>
                      </div>
                    )}
                  </div>
                )}

                {patternKeys.map((key) => {
                  const pattern = examPattern[key];
                  const keyTitle = key.replace(/([A-Z0-9])/g, " $1").replace(/^./, (s) => s.toUpperCase()).replace(/\b(tier|paper)\b/gi, (m) => m.charAt(0).toUpperCase() + m.slice(1));
                  const hasSections = Array.isArray(pattern.sections);
                  const hasWeightage = hasSections && pattern.sections.some((s) => s.weightage);
                  // Sum numeric questions only
                  const totalQ = hasSections ? pattern.sections.reduce((a, s) => a + (typeof s.questions === "number" ? s.questions : 0), 0) : 0;
                  return (
                    <div key={key} className="mb-8" data-testid={`exam-pattern-${key}`}>
                      <h3 className="font-heading font-semibold text-lg text-vedanti-navy mb-3 capitalize flex items-center gap-2 flex-wrap">
                        <div className="w-2 h-2 bg-vedanti-orange rounded-full"></div>
                        <span>{keyTitle}</span>
                        {pattern.totalMarks && <span className="text-sm font-normal text-slate-500">— {pattern.totalMarks} Marks {pattern.totalTime ? `(${pattern.totalTime})` : ""}</span>}
                      </h3>

                      {/* Metadata strip */}
                      {(pattern.nature || pattern.mode || pattern.sectionalTiming || pattern.negativeMarking) && (
                        <div className="flex flex-wrap gap-2 mb-3 text-xs">
                          {pattern.nature && <span className="px-2.5 py-1 rounded-full bg-amber-50 text-amber-800 font-semibold border border-amber-100">{pattern.nature}</span>}
                          {pattern.mode && <span className="px-2.5 py-1 rounded-full bg-blue-50 text-blue-800 font-medium border border-blue-100">Mode: {pattern.mode}</span>}
                          {pattern.sectionalTiming && <span className="px-2.5 py-1 rounded-full bg-purple-50 text-purple-800 font-medium border border-purple-100">Sectional Timing: {pattern.sectionalTiming}</span>}
                          {pattern.negativeMarking && <span className="px-2.5 py-1 rounded-full bg-red-50 text-red-800 font-medium border border-red-100">Negative: {pattern.negativeMarking}</span>}
                        </div>
                      )}

                      {hasSections ? (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm" data-testid={`exam-table-${key}`}>
                            <thead>
                              <tr className="bg-vedanti-navy text-white">
                                <th className="text-left px-4 py-3 rounded-tl-lg">Section</th>
                                <th className="text-center px-4 py-3">Questions</th>
                                <th className="text-center px-4 py-3">Marks</th>
                                <th className={`text-center px-4 py-3 ${hasWeightage ? "" : "rounded-tr-lg"}`}>Time</th>
                                {hasWeightage && <th className="text-center px-4 py-3 rounded-tr-lg">Weightage</th>}
                              </tr>
                            </thead>
                            <tbody>
                              {pattern.sections.map((sec, idx) => (
                                <tr key={sec.name} className={`border-b border-slate-100 ${idx % 2 === 0 ? "bg-white" : "bg-slate-50"}`}>
                                  <td className="px-4 py-3 text-slate-700 font-medium">{sec.name}</td>
                                  <td className="px-4 py-3 text-center text-slate-600">{sec.questions}</td>
                                  <td className="px-4 py-3 text-center text-slate-600">{sec.marks}</td>
                                  <td className="px-4 py-3 text-center text-slate-600">{sec.time === "Cumulative" ? <span className="text-xs text-slate-400 italic">Cumulative</span> : sec.time}</td>
                                  {hasWeightage && <td className="px-4 py-3 text-center text-slate-600">{sec.weightage || "—"}</td>}
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr className="bg-vedanti-navy/5 font-semibold">
                                <td className="px-4 py-3 text-vedanti-navy">Total</td>
                                <td className="px-4 py-3 text-center text-vedanti-navy">{pattern.totalQuestions || totalQ || "—"}</td>
                                <td className="px-4 py-3 text-center text-vedanti-navy">{pattern.totalMarks || "—"}</td>
                                <td className="px-4 py-3 text-center text-vedanti-navy font-bold">
                                  {pattern.sections.some(s => s.time === "Cumulative")
                                    ? `Cumulative (${pattern.totalTime})`
                                    : (pattern.totalTime || "—")}
                                </td>
                                {hasWeightage && <td className="px-4 py-3 text-center text-vedanti-navy">100%</td>}
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      ) : (
                        /* Single-paper layout (e.g., Tier 2 Paper 2 / Paper 3) */
                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                          {pattern.subject && <div className="bg-white border border-slate-200 rounded-lg p-3"><p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Subject</p><p className="text-sm font-bold text-vedanti-navy mt-1">{pattern.subject}</p></div>}
                          {pattern.questions !== undefined && <div className="bg-white border border-slate-200 rounded-lg p-3"><p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Questions</p><p className="text-sm font-bold text-vedanti-navy mt-1">{pattern.questions}</p></div>}
                          {pattern.marks && <div className="bg-white border border-slate-200 rounded-lg p-3"><p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Marks</p><p className="text-sm font-bold text-vedanti-navy mt-1">{pattern.marks}</p></div>}
                          {pattern.time && <div className="bg-white border border-slate-200 rounded-lg p-3"><p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Duration</p><p className="text-sm font-bold text-vedanti-navy mt-1">{pattern.time}</p></div>}
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Important posts (if available) */}
                {course.importantPosts && course.importantPosts.length > 0 && (
                  <div className="mt-6 bg-slate-50 rounded-xl p-5 border border-slate-100">
                    <h3 className="font-heading font-semibold text-base text-vedanti-navy mb-3">Posts Offered</h3>
                    <div className="flex flex-wrap gap-2">
                      {course.importantPosts.map((p) => (
                        <span key={p} className="px-3 py-1.5 text-xs font-medium bg-white border border-slate-200 rounded-full text-slate-700">{p}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Syllabus */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-vedanti-navy mb-4">Complete Syllabus</h2>
                <p className="text-slate-500 text-sm mb-5">Detailed topic-wise syllabus covered in our {course.name} coaching program at Vedanti Academy</p>
                <div className="space-y-4">
                  {Object.entries(course.syllabus).map(([section, topics], i) => {
                    const colors = ["border-vedanti-orange", "border-vedanti-navy", "border-green-500", "border-vedanti-mango", "border-blue-500", "border-purple-500"];
                    const bgColors = ["bg-orange-50", "bg-blue-50", "bg-green-50", "bg-amber-50", "bg-sky-50", "bg-purple-50"];
                    const dotColors = ["bg-vedanti-orange", "bg-vedanti-navy", "bg-green-500", "bg-vedanti-mango", "bg-blue-500", "bg-purple-500"];
                    return (
                      <div key={section} className={`rounded-xl border-l-4 ${colors[i % colors.length]} ${bgColors[i % bgColors.length]} p-4`} data-testid={`syllabus-group-${i}`}>
                        <h3 className="font-heading font-bold text-vedanti-navy text-base mb-3 flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${dotColors[i % dotColors.length]}`}></div>
                          {section}
                          <span className="text-xs font-normal text-slate-400 ml-auto">{topics.length} topics</span>
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                          {topics.map((topic) => (
                            <div key={topic} className="flex items-start gap-2 px-2 py-1.5">
                              <CheckCircle size={13} className="text-vedanti-orange flex-shrink-0 mt-0.5" />
                              <span className="text-slate-700 text-sm leading-snug">{topic}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Why Vedanti */}
              <div className="bg-gradient-to-br from-vedanti-navy to-blue-900 rounded-2xl p-6 text-white">
                <h2 className="font-heading text-xl font-bold mb-4">Why Vedanti Academy for {course.name}?</h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  {[
                    "Expert faculty with years of competitive exam coaching experience",
                    "Vedic Math & unique shortcut techniques for faster calculations",
                    "AC classrooms equipped with digital smart boards",
                    "360-degree hybrid doubt support (offline + WhatsApp/Zoom)",
                    "'Upto Selection Support' — we coach you until you crack it",
                    "Comprehensive mock test series simulating real exam conditions",
                    "In-house library with all reference books & previous papers",
                    "Regular performance tracking & personalized improvement plans",
                  ].map((point) => (
                    <div key={point} className="flex items-start gap-2">
                      <CheckCircle size={15} className="text-vedanti-mango flex-shrink-0 mt-0.5" />
                      <span className="text-slate-200 text-sm leading-snug">{point}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* CTA Card */}
              <div className="bg-vedanti-navy rounded-2xl p-6 text-white sticky top-24" data-testid="course-sidebar-cta">
                <h3 className="font-heading font-bold text-xl mb-2">Enroll in {course.name}</h3>
                <p className="text-slate-300 text-sm mb-4">{course.batchInfo}</p>
                <div className="space-y-3">
                  <Link
                    to="/demo-class"
                    className="block text-center bg-vedanti-orange hover:bg-vedanti-orange/90 text-white rounded-lg px-5 py-3 font-semibold transition-all"
                    data-testid="course-demo-btn"
                  >
                    Book Free Demo Class
                  </Link>
                  <Link
                    to="/consultancy"
                    className="block text-center border border-white/30 hover:bg-white/10 text-white rounded-lg px-5 py-3 font-medium transition-colors"
                    data-testid="course-consultancy-btn"
                  >
                    Book Consultancy
                  </Link>
                  <a
                    href="tel:+918308776505"
                    className="flex items-center justify-center gap-2 text-vedanti-mango hover:text-vedanti-orange py-2 font-medium transition-colors"
                    data-testid="course-call-btn"
                  >
                    <Phone size={18} /> +91 8308776505
                  </a>
                  <a
                    href="https://wa.me/918308776505?text=Hi%2C%20I%27m%20interested%20in%20coaching%20at%20Vedanti%20Academy."
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="flex items-center justify-center gap-2 text-green-400 hover:text-green-300 py-2 font-medium transition-colors"
                    data-testid="course-whatsapp-btn"
                  >
                    <MessageCircle size={18} /> WhatsApp Us
                  </a>
                </div>
              </div>

              {/* Related Courses */}
              {relatedCourses.length > 0 && (
                <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
                  <h3 className="font-heading font-bold text-lg text-vedanti-navy mb-4">Related Courses</h3>
                  <div className="space-y-3">
                    {relatedCourses.map((rc) => (
                      <Link
                        key={rc.slug}
                        to={`/courses/${rc.slug}`}
                        className="flex items-center justify-between p-3 bg-white rounded-lg hover:shadow-sm transition-all group"
                      >
                        <span className="text-sm text-slate-700 font-medium group-hover:text-vedanti-orange">{rc.name}</span>
                        <ChevronRight size={14} className="text-slate-400 group-hover:text-vedanti-orange" />
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
